import { useAppStore } from '@/dashboard/store/appStore';
import { storeToRefs } from 'pinia';
import { computed, ComputedRef } from 'vue';
import { useRouter } from 'vue-router';
import { ProjectWithUser } from '@/dashboard/types/project';
import { UserRole, UserRoleType } from '@/dashboard/types/user_role';
import { Step, StepType } from '@/dashboard/types/step';
import { openEditProject } from '@/dashboard/utils/project/openEditProject';
import { ProjectApi } from '@/dashboard/api/project';
import { useAlertStore } from '@/dashboard/store/alertStore';

export function useDirectoryContextMenu(id: number) {
  const appStore = useAppStore();
  const alertStore = useAlertStore();
  const router = useRouter();

  const { workspaces, sharedProjects, baseWorkspaceShortLink } = storeToRefs(appStore);
  const project: ComputedRef<ProjectWithUser | undefined> = computed(() => {
    return [...workspaces.value.map((w) => w.projects).flat(), ...sharedProjects.value].find((item) => item.id === id);
  });
  const currentProjectUser = computed(() => project.value?.user);
  const canEdit = computed(() => {
    const roles: UserRoleType[] = [UserRole.Owner, UserRole.Admin];
    const role: UserRoleType | undefined = currentProjectUser.value?.role;

    return !!role && roles.includes(role) && !project.value?.temporary;
  });
  const canDelete = computed(() => {
    const roles: UserRoleType[] = [UserRole.Owner, UserRole.Admin];
    const role = currentProjectUser.value?.role;

    return !!role && roles.includes(role) && !project.value?.temporary;
  });
  const canLeave = computed(() => {
    const roles: UserRoleType[] = [UserRole.Creator, UserRole.Viewer, UserRole.Client];
    const role = currentProjectUser.value?.role;

    return !!role && roles.includes(role) && !project.value?.temporary;
  });
  const menuItems = computed(() => {
    return [
      {
        canRender: canEdit.value,
        action: editProject,
        icon: 'SettingsIcon',
        label: 'Settings',
        labelClass: 'text-black dark:text-white',
        iconClass: 'stroke-black dark:stroke-white',
      },
      {
        canRender: canLeave.value,
        action: openConfirmAlert,
        icon: 'LogoutIcon',
        label: 'Leave directory',
        labelClass: 'text-black dark:text-white',
        iconClass: 'stroke-black dark:stroke-white',
      },
      {
        canRender: false,
        action: () => {},
        icon: 'ArchiveIcon',
        label: 'Archive directory',
        labelClass: 'text-black dark:text-white',
        iconClass: 'stroke-black dark:stroke-white',
      },
      {
        canRender: canDelete.value,
        action: openDeleteProjectPopup,
        icon: 'DeleteIcon',
        label: 'Delete',
        labelClass: 'text-hard-error',
        iconClass: 'stroke-hard-error',
      },
    ].filter((item) => item.canRender);
  });

  async function editProject(step: StepType = Step.Name) {
    if (project.value) {
      await openEditProject(project.value.short_link, step);
    }
  }

  async function openDeleteProjectPopup() {
    const proj = project.value;
    if (proj) {
      appStore.openDeleteProjectPopup(proj);
    }
  }

  async function leaveProject() {
    if (project.value && currentProjectUser.value) {
      const result = await ProjectApi.deleteUser({
        projectShortLink: project.value.short_link,
        userId: currentProjectUser.value.id,
      });
      if (result.success && result.project) {
        await appStore.loadWorkspaces();

        const workspace = appStore.workspaces.find((item) => item.id === result.project?.workspace_id);
        await router.push({
          name: 'workspace',
          params: {
            short_link: workspace ? workspace.short_link : baseWorkspaceShortLink.value,
          },
        });
      }
    }
  }

  function openConfirmAlert() {
    alertStore.open({
      title: 'Leave Directory Confirmation',
      subtitle: 'Are you sure you want to leave this directory?',
      action: leaveProject,
      cancelAction: () => {},
    });
  }

  return {
    menuItems,
  };
}
